import React, { Component, ErrorInfo, ReactNode } from 'react';
import ErrorFallback from './ErrorFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, resetError: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'global' | 'route' | 'component';
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { level = 'component', componentName, onError } = this.props;

    console.error(
      `[ErrorBoundary] ${level} error${componentName ? ` in ${componentName}` : ''}:`,
      error,
      errorInfo
    );

    this.setState({
      errorInfo,
    });

    if (onError) {
      onError(error, errorInfo);
    }

    if (level === 'global') {
      try {
        localStorage.setItem('last_error', JSON.stringify({
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString(),
          componentStack: errorInfo.componentStack,
        }));
      } catch (e) {
        console.error('[ErrorBoundary] Failed to save error to localStorage:', e);
      }
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback, level = 'component', componentName } = this.props;

    if (hasError && error) {
      if (fallback) {
        return fallback(error, this.resetError);
      }

      const showHomeButton = level === 'route' || level === 'global';
      const title = level === 'global'
        ? 'Error crítico'
        : level === 'route'
        ? 'Error en esta página'
        : componentName
        ? `Error en ${componentName}`
        : 'Error';

      const message = level === 'global'
        ? 'La aplicación encontró un error crítico y necesita recargarse.'
        : level === 'route'
        ? 'Esta página encontró un error. Puedes intentar recargar o volver al inicio.'
        : 'Este componente encontró un error. Intenta recargar la página.';

      return (
        <ErrorFallback
          error={error}
          resetError={this.resetError}
          showHomeButton={showHomeButton}
          title={title}
          message={message}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
